import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from '../../wrappers/ReactRouterDom';

import useSecurityData from '../../state/security/UseSecurityData';
import useDateTimeData from '../../state/datetime/UseDateTimeData';
import useGlobalRoutesData from '../../state/security/UseGlobalRoutesData';
import useClubPersistentStorage from '../../state/clubPersistentStorage/UseClubPersistentStorage';
import useMemberPersistentStorage from '../../state/memberPersistentStorage/UseMemberPersistentStorage';
import usePersonAcknowledgementData from '../../state/personAcknowledgement/UsePersonAcknowledgementData';

import { getStoredTokenObj, resetStoredToken } from '../../utils/TokenUtils';
import { clearUserInfoCache } from '../../utils/UserInfoCache';
import { getConfig } from '../../config/RuntimeConfig';

const SESSION_TIMEOUT_WARNING_MS = 5 * 60 * 1000;
const SESSION_INTERVAL_CHECK_MS = 30 * 1000;

const INITIAL_SESSION_STATE = {
  showSessionEnding: false,
  disableSessionEndingPopup: false
};

let logoutTimeout = null;

const usePageTemplate = () => {
  const location = useLocation();
  const [searchParams,] = useSearchParams();
  const { securityState, currentNavItem, userInfo,
    closeSecondaryNav, verifyUser
  } = useSecurityData();
  const { getDateTimeData } = useDateTimeData();
  const { getGlobalRoutesData, globalRoutesState } = useGlobalRoutesData();
  const { resetExpiration } = useMemberPersistentStorage();
  const { resetClubExpiration } = useClubPersistentStorage();
  const { personAcknowledgementState, getPersonAcknowledgements } = usePersonAcknowledgementData(true);
  const [sessionState, setSessionState] = useState(INITIAL_SESSION_STATE);
  const [isLoading, setIsLoading] = useState(true);

  const hasBeenClicked = () => {
    if (currentNavItem?.displayName) {
      closeSecondaryNav();
    }
  };

  const logout = () => {
    clearUserInfoCache();
    resetStoredToken();
    const authUrl = `${getConfig().VITE_YARP_BASE_URL}/bff/logout?returnUrl=` + encodeURIComponent(`${getConfig().VITE_YARP_BASE_URL}/landing`);
    window.location.href = authUrl;
  };


  const setShowSessionEnding = (showSessionEnding, disableSessionEndingPopup = false) => {
    setSessionState({
      ...sessionState,
      showSessionEnding,
      disableSessionEndingPopup
    });
  };

  useEffect(() => {
    if (securityState.isObjLoading !== true) {
      verifyUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location]);


  useEffect(() => {
    if (globalRoutesState.isArrayLoaded) {
      getDateTimeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalRoutesState.arrayData]);

  useEffect(() => {
    getGlobalRoutesData();
    if (getConfig().VITE_MEMBER_UI_DOMAIN === 'self') {
      resetExpiration();
    } else if (getConfig().VITE_CLUB_UI_DOMAIN === 'self') {
      resetClubExpiration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (securityState.isObjLoaded === true) {
      if (!userInfo.memberId || userInfo.memberId === 'Unregistered') {
        setIsLoading(false);
      } else if (personAcknowledgementState.isArrayLoaded !== true) {
        getPersonAcknowledgements(userInfo.memberId);
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personAcknowledgementState, securityState]);

  return {
    isLoading,
    memberId: userInfo.memberId,
    isLoggedIn: userInfo?.isLoggedIn === true,
    showSessionEnding: sessionState.showSessionEnding,
    isUnregisteredUser: userInfo.memberId === 'Unregistered',
    hasConfirmedContactInfo:  userInfo?.hasConfirmedContactInfo,
    isPendingParentConsent: userInfo?.pendingParentConsent,
    areAcknowledgementsComplete: personAcknowledgementState.isArrayLoaded === true && personAcknowledgementState.arrayData.length === 0,
    hasBeenClicked,
    setShowSessionEnding
  };
};

export default usePageTemplate;